import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, BookOpen, ArrowRight, FolderOpen, Layers } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import ClientDocumentsPortal from "../components/ClientDocumentsPortal";

export default function Dashboard() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const [activeTab, setActiveTab] = useState("documents"); // documents | bookings | courses
  const [bookings, setBookings] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState({});

  useEffect(() => {
    if (!user) return;
    api.get("/me/bookings").then((r) => setBookings(Array.isArray(r.data) ? r.data : [])).catch(() => setBookings([]));
    api.get("/me/enrollments").then((r) => setEnrollments(Array.isArray(r.data) ? r.data : [])).catch(() => setEnrollments([]));
    api.get("/courses").then((r) => {
      const map = {};
      if (Array.isArray(r.data)) {
        r.data.forEach((c) => {
          if (c?.course_id) map[c.course_id] = c;
        });
      }
      setCourses(map);
    }).catch(() => setCourses({}));
  }, [user]);

  if (loading) return <div className="max-w-[1400px] mx-auto px-6 py-32 text-navy/60">{t("common.loading")}</div>;
  if (!user) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-32 text-center">
        <p className="text-navy/70 mb-6">{lang === "fr" ? "Connectez-vous pour voir votre espace." : "Sign in to see your space."}</p>
        <Link to="/login" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-navy text-alabaster text-sm hover:bg-terracotta transition">
          {t("nav.login")} <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-alabaster min-h-[80vh]" data-testid="dashboard-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-8">
        <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-3">— {t("portal.welcome")}</p>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            {user.picture && <img src={user.picture} alt={user.name} className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover shadow-xs border border-navy/10" data-testid="user-avatar" />}
            <div>
              <h1 className="font-serif text-3xl sm:text-4xl text-navy tracking-tight" data-testid="user-name">{user.name}</h1>
              <p className="text-navy/60 text-xs sm:text-sm" data-testid="user-email">{user.email}</p>
            </div>
          </div>

          {/* Tab Selector */}
          <div className="flex items-center gap-1.5 p-1 bg-navy/5 rounded-2xl border border-navy/10 self-start sm:self-auto overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab("documents")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition ${
                activeTab === "documents"
                  ? "bg-navy text-white shadow-xs"
                  : "text-navy/70 hover:text-navy hover:bg-navy/5"
              }`}
            >
              <FolderOpen size={15} />
              <span>{lang === "fr" ? "Mon Dossier & Documents" : "My Documents & File"}</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("bookings")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition ${
                activeTab === "bookings"
                  ? "bg-navy text-white shadow-xs"
                  : "text-navy/70 hover:text-navy hover:bg-navy/5"
              }`}
            >
              <Calendar size={15} />
              <span>{t("portal.my_bookings")}</span>
              {bookings.length > 0 && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeTab === "bookings" ? "bg-white/20 text-white" : "bg-navy/10 text-navy"}`}>
                  {bookings.length}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("courses")}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition ${
                activeTab === "courses"
                  ? "bg-navy text-white shadow-xs"
                  : "text-navy/70 hover:text-navy hover:bg-navy/5"
              }`}
            >
              <BookOpen size={15} />
              <span>{t("portal.my_courses")}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
        {activeTab === "documents" && (
          <ClientDocumentsPortal />
        )}

        {activeTab === "bookings" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-navy/8 p-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs tracking-[0.25em] uppercase text-terracotta">{t("portal.my_bookings")}</p>
              <Calendar size={18} className="text-navy/40" />
            </div>
            {bookings.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-navy/50 italic mb-4">{t("portal.no_bookings")}</p>
                <Link to="/booking" className="inline-flex items-center gap-2 text-sm text-terracotta link-underline">{t("nav.book")} <ArrowRight size={14} /></Link>
              </div>
            ) : (
              <ul className="divide-y divide-navy/10">
                {bookings.map((b) => (
                  <li key={b.booking_id} className="py-4 flex items-start justify-between gap-4" data-testid={`booking-item-${b.booking_id}`}>
                    <div>
                      <p className="font-serif text-lg text-navy leading-tight">{b.topic}</p>
                      <p className="text-xs text-navy/60 mt-1">{new Date(b.slot_start).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</p>
                    </div>
                    <span className={`text-[10px] tracking-[0.2em] uppercase px-2 py-1 rounded-full ${b.status === "confirmed" ? "bg-cream text-terracotta" : "bg-navy/5 text-navy/60"}`}>{b.status}</span>
                  </li>
                ))}
              </ul>
            )}
          </motion.div>
        )}

        {activeTab === "courses" && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl border border-navy/8 p-8">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs tracking-[0.25em] uppercase text-terracotta">{t("portal.my_courses")}</p>
              <BookOpen size={18} className="text-navy/40" />
            </div>
            {enrollments.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-navy/50 italic mb-4">{t("portal.no_courses")}</p>
                <Link to="/tcf" className="inline-flex items-center gap-2 text-sm text-terracotta link-underline">{t("portal.view_courses")} <ArrowRight size={14} /></Link>
              </div>
            ) : (
              <ul className="divide-y divide-navy/10">
                {enrollments.map((e) => {
                  const c = courses[e.course_id];
                  return (
                    <li key={e.enrollment_id} className="py-4" data-testid={`enrollment-${e.enrollment_id}`}>
                      <p className="font-serif text-xl text-navy">{c ? (lang === "fr" ? c.title_fr : c.title_en) : e.course_id}</p>
                      <p className="text-xs text-terracotta tracking-[0.2em] uppercase mt-2">{lang === "fr" ? "Actif" : "Active"}</p>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </section>
    </div>
  );
}

